import { useState, useEffect, useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import isEmpty from "lodash/isEmpty";

import { testTypeSchema } from "../../yup";
import { useUpdateTestTypeDetailsMutation, useGetSpecialitiesQuery, useGetTestTypeDetailQuery } from "../../services/api";
import style from "./testType.module.css";
import { Button, LabelBox } from "../../components";
import { colors } from "../../theme/colors";
import TestTypeFormFields from "./TestTypeFormFields";
import { mapSpecialityIdsToItems } from "./testTypeUtils";
import type { ApiError, SpecialityOption, TestTypeFormValues } from "./testTypeTypes";
import type { Speciality } from "../../types/index";

type FormState = TestTypeFormValues;

/**
 * Renders the Edit Test Type form for an existing test type.
 *
 * The component loads the test type details and specialities, hydrates the
 * form, and submits an update mutation on save.
 *
 * Returns:
 *   JSX.Element: The Edit Test Type page content.
 */
const EditTestType = () => {
  const [updateTestType] = useUpdateTestTypeDetailsMutation();
  const { id } = useParams();

  const { data: specialityData = { items: [] }, isLoading: isSpecialityLoading } = useGetSpecialitiesQuery({
    order_by_columns: "name",
    order: "Asc",
    is_detailed: true,
    search_key: ""
  });

  const specialities = useMemo<SpecialityOption[]>(
    () => specialityData?.items?.map((speciality: Speciality) => ({
      id: speciality?.id,
      label: speciality?.name,
      value: speciality?.name,
    })) ?? [],
    [specialityData]
  );

  const {
    data: testTypeDetails,
    isLoading,
    isFetching,
  } = useGetTestTypeDetailQuery(id ?? "", { skip: !id });

  const navigate = useNavigate();

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [isSavingTestType, setIsSavingTestType] = useState(false);
  const mappedSpecialityDetails = useMemo<Speciality[]>(
    () => mapSpecialityIdsToItems(testTypeDetails?.speciality_ids, specialityData?.items),
    [specialityData, testTypeDetails]
  );

  /*
   * useForm hook from react-hook-form
   * Manages form state, validation, and submission logic.
   * - register: Registers input fields and gathers their values.
   * - handleSubmit: Triggers the provided callback on form submission.
   * - formState: Provides access to form state, such as errors
   * - yupResolver(testTypeSchema): Restrict the type of payload data for required, min length etc
   */
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    watch,
    reset,
    setValue
  } = useForm<FormState>({
    resolver: yupResolver<TestTypeFormValues>(testTypeSchema),
    mode: "onChange",
  });

  useEffect(() => {
    // Hydrate form once details are available; allow empty speciality mappings.
    if (!isEmpty(testTypeDetails) && !isSpecialityLoading) {
      reset({
        name: testTypeDetails?.name,
        speciality_ids: mappedSpecialityDetails?.map((speciality: Speciality) => ({
          id: speciality?.id,
          label: speciality?.name,
          value: speciality?.name,
        })),
        info: testTypeDetails?.info,
        specimen_storage: testTypeDetails?.specimen_storage
      });
      setValue("is_intra_operative", testTypeDetails?.is_intra_operative ?? false);
    }
  }, [mappedSpecialityDetails, testTypeDetails, reset, setValue, isSpecialityLoading]);

  const onSubmit = async (values: FormState) => {
    if (!id) {
      return;
    }
    try {
      setIsSavingTestType(true);
      const { name, speciality_ids, is_intra_operative, info, specimen_storage } = values;
      const payload: EditTestTypePayload = {
        id: parseInt(id, 10),
        name: name,
        speciality_ids: speciality_ids?.map((speciality) => speciality.id) ?? [],
        is_intra_operative: is_intra_operative ?? false,
        info: info ?? "",
        specimen_storage: specimen_storage
      };

      await updateTestType(payload).unwrap();
      setIsSavingTestType(false);
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (error) {
      const apiError = error as ApiError;
      setMessage({
        type: "error",
        text: apiError?.data?.detail?.toString() || "Please enter valid details",
      });
      setIsSavingTestType(false);
    }
  };

  return (
    <Box className={style.container}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        {(isFetching || isLoading || isSpecialityLoading) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100vh",
              width: "calc(100vw - 400px)",
              zIndex: 999,
              backgroundColor: "transparent",
              position: "absolute",
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
        <Box>
          <Typography
            className={style.detailHeading2}
            sx={{ marginTop: "10px" }}
          >
            Test Type Details
          </Typography>
          <Box className={style.flexTop}>
            <TestTypeFormFields
              register={register}
              errors={errors}
              control={control}
              getValues={getValues}
              watch={watch}
              setValue={setValue}
              specialities={specialities}
            />
          </Box>
          {message && (
            <Box mt={2}>
              <LabelBox
                boxProps={{ height: 50, display: "flex", alignItems: "center" }}
                data={message}
                onClose={() => setMessage(null)}
              />
            </Box>
          )}
          <Box className={style.buttons}>
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: "transparent" },
            }}
            variant="outlined"
            onClick={() => navigate(-1)}
            label={"Back"}
          />
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: colors.primary },
              }}
              variant="contained"
              type="submit"
              label={"Save"}
              loading={isSavingTestType}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default EditTestType;
